
import { useState } from "react";
import { Application } from "@/types/application";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { BasicConfigTab } from "./config/BasicConfigTab";
import { AppSpecificTab } from "./config/AppSpecificTab";
import { AppConfigurationTab } from "./config/AppConfigurationTab";
import { PPPCSettingsForm } from "./pppc/PPPCSettingsForm";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface ConfigureApplicationDialogProps {
  application: Application;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (application: Application) => void;
}

export const ConfigureApplicationDialog = ({
  application,
  open,
  onOpenChange,
  onSave
}: ConfigureApplicationDialogProps) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const isMacOS = application.platform === "macos";
  
  // Initialize with default values or existing values
  const defaultPppcSettings = {
    codeRequirement: application.pppcSettings?.codeRequirement || "",
    identifierType: application.pppcSettings?.identifierType || "bundleID",
    privacyPermissions: application.pppcSettings?.privacyPermissions || []
  };
  
  const form = useForm({
    defaultValues: {
      pppcSettings: defaultPppcSettings
    },
    // Make sure the form is initialized properly
    mode: "onBlur"
  });

  const validateConfiguration = () => {
    const errors: string[] = [];
    const formValues = form.getValues();
    
    // Only validate PPPC settings if we're on macOS and on the PPPC tab
    if (isMacOS && activeTab === "pppc") {
      if (!formValues.pppcSettings.codeRequirement?.trim()) {
        errors.push("Code Requirement is required for PPPC settings");
      }
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSave = () => {
    if (!validateConfiguration()) {
      return;
    }

    const formValues = form.getValues();
    
    const updatedApp = {
      ...application,
      pppcSettings: formValues.pppcSettings
    };
    
    onSave(updatedApp);
    toast.success("Application configuration saved");
    onOpenChange(false);
  };

  // Only show PPPC settings tab for macOS applications
  const showPPPC = isMacOS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 rounded overflow-hidden">
              <img src={application.icon} alt={application.name} className="w-full h-full object-cover" />
            </div>
            Configure {application.name}
          </DialogTitle>
          <DialogDescription>
            Configure application settings{showPPPC ? ", privacy preferences," : ""} and deployment options
          </DialogDescription>
        </DialogHeader>

        {validationErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="basic" className="flex-1">Basic Configuration</TabsTrigger>
              {showPPPC && (
                <TabsTrigger value="pppc" className="flex-1">Privacy Preferences (PPPC)</TabsTrigger>
              )}
              <TabsTrigger value="appconfig" className="flex-1">App Configuration</TabsTrigger>
              <TabsTrigger value="specific" className="flex-1">App-Specific Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="pt-4">
              <BasicConfigTab />
            </TabsContent>

            {showPPPC && (
              <TabsContent value="pppc" className="pt-4">
                <PPPCSettingsForm form={form} />
              </TabsContent>
            )}

            <TabsContent value="appconfig" className="pt-4">
              <AppConfigurationTab application={application} />
            </TabsContent>

            <TabsContent value="specific" className="pt-4">
              <AppSpecificTab />
            </TabsContent>
          </Tabs>
        </Form>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
